import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  Receipt,
  IndianRupee,
  Percent,
  FileDown,
  Edit3,
  Calendar,
  User,
  CheckCircle2,
  Sparkles
} from "lucide-react";
import { generateInvoicePdf } from "@/utils/generateInvoicePdf";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FinancialsPage({ businessInfo }) {
  const { id } = useParams();
  const [booking, setBooking] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadBooking = async () => {
      setIsLoading(true);
      try {
        const res = await axios.get(`${API}/bookings/${id}`);
        setBooking(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };
    loadBooking();
  }, [id]);

  if (isLoading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-pink-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="bg-white p-12 rounded-3xl border border-pink-100 text-center space-y-4">
        <h2 className="text-xl font-bold text-pink-950">Booking Not Found</h2>
        <Link
          to="/bookings"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-pink-900 text-white text-xs font-bold"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Bookings</span>
        </Link>
      </div>
    );
  }

  const hallRent = booking.hall_rent ?? booking.hallRent ?? booking.rent_amount ?? (booking.totalAmount || booking.total_amount || 0);
  const labor = Number(booking.labor_charges ?? booking.laborCharges ?? 0);
  const electricity = Number(booking.electricity_charges ?? booking.electricityCharges ?? 0);
  const maintenance = Number(booking.maintenance_fee ?? booking.maintenanceFee ?? 0);
  const applyGst = Boolean(booking.apply_gst || booking.applyGst || booking.sgst_amount > 0 || booking.cgst_amount > 0);
  const sgst = Number(booking.sgst_amount ?? booking.sgstAmount ?? (applyGst ? hallRent * 0.09 : 0));
  const cgst = Number(booking.cgst_amount ?? booking.cgstAmount ?? (applyGst ? hallRent * 0.09 : 0));
  const totalGst = sgst + cgst;
  const additionalCharges = booking.additional_charges || [];
  const additionalTotal = additionalCharges.reduce((acc, c) => acc + (parseFloat(c.amount) || 0), 0);
  const total = Number(booking.totalAmount ?? booking.total_rent ?? booking.total_amount ?? booking.rent_amount ?? 0);
  const advance = Number(booking.advancePaid ?? booking.deposit_amount ?? booking.advance_paid ?? booking.advance_amount ?? 0);
  const balance = booking.balanceDue !== undefined ? Number(booking.balanceDue) : (booking.balance_due !== undefined ? Number(booking.balance_due) : Math.max(0, total - advance));

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl mx-auto">
      {/* Top Header with Back Navigation */}
      <div className="flex items-center justify-between">
        <Link
          to={`/bookings/${id}`}
          className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold text-pink-900 hover:text-pink-950 bg-white px-4 py-2 rounded-xl border border-pink-200 shadow-sm transition-all"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Booking Details</span>
        </Link>

        <div className="flex items-center gap-2">
          <Link
            to={`/bookings/${id}/edit`}
            className="px-4 py-2 rounded-xl bg-white hover:bg-pink-50 text-pink-900 border border-pink-200 text-xs font-bold shadow-sm flex items-center gap-1.5 transition-colors"
          >
            <Edit3 className="w-3.5 h-3.5" />
            <span>Edit Charges</span>
          </Link>
          <button
            onClick={() => generateInvoicePdf(booking, businessInfo)}
            className="px-4 py-2 rounded-xl bg-pink-900 hover:bg-pink-950 text-white text-xs font-bold shadow flex items-center gap-1.5 transition-colors"
          >
            <FileDown className="w-3.5 h-3.5" />
            <span>Download Invoice</span>
          </button>
        </div>
      </div>

      {/* Main Financial Card */}
      <div className="bg-white rounded-3xl border border-pink-100 shadow-xl overflow-hidden">
        {/* Banner */}
        <div className="bg-gradient-to-r from-pink-900 via-pink-800 to-rose-800 p-6 sm:p-8 text-white">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/20 text-xs font-bold mb-2">
            <Receipt className="w-3.5 h-3.5 text-pink-200" />
            <span>Itemized Billing & Tax Breakdown</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-black">{booking.client_name} — Financial Details</h1>
          <p className="text-pink-100 text-xs sm:text-sm mt-1">
            Event: {booking.event_type} • Dates: {booking.start_date === booking.end_date ? booking.start_date : `${booking.start_date} to ${booking.end_date}`}
          </p>
        </div>

        {/* Content */}
        <div className="p-6 sm:p-8 space-y-6">
          
          {/* Base Charges Table */}
          <div className="space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-wider text-pink-950">
              Base Venue & Utility Charges
            </h3>
            
            <div className="overflow-hidden rounded-2xl border border-pink-100">
              <table className="w-full text-left text-xs">
                <thead className="bg-pink-50/60 border-b border-pink-100 font-bold text-pink-950">
                  <tr>
                    <th className="py-3 px-4">Item Description</th>
                    <th className="py-3 px-4">Rate Basis</th>
                    <th className="py-3 px-4 text-right">Amount (INR)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-pink-50 text-gray-700">
                  <tr>
                    <td className="py-3.5 px-4 font-bold text-gray-900">Convention Hall Rent</td>
                    <td className="py-3.5 px-4 text-gray-500">Base Venue Booking Slot</td>
                    <td className="py-3.5 px-4 text-right font-bold font-mono">₹{Number(hallRent).toLocaleString("en-IN")}</td>
                  </tr>
                  <tr>
                    <td className="py-3.5 px-4 font-bold text-gray-900">Labor Charges</td>
                    <td className="py-3.5 px-4 text-gray-500">Staff, Cleaning & Venue Setup</td>
                    <td className="py-3.5 px-4 text-right font-bold font-mono">₹{Number(labor).toLocaleString("en-IN")}</td>
                  </tr>
                  <tr>
                    <td className="py-3.5 px-4 font-bold text-gray-900">Electricity & Utility Charges</td>
                    <td className="py-3.5 px-4 text-gray-500">Standard Consumption / Metering</td>
                    <td className="py-3.5 px-4 text-right font-bold font-mono">₹{Number(electricity).toLocaleString("en-IN")}</td>
                  </tr>
                  <tr>
                    <td className="py-3.5 px-4 font-bold text-gray-900">Maintenance Fee</td>
                    <td className="py-3.5 px-4 text-gray-500">Facility Upkeep & Sanitation</td>
                    <td className="py-3.5 px-4 text-right font-bold font-mono">₹{Number(maintenance).toLocaleString("en-IN")}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* 18% GST Breakdown */}
          {applyGst && (
            <div className="p-5 rounded-2xl bg-pink-50/70 border border-pink-200 space-y-3">
              <div className="flex items-center gap-2 text-pink-950 font-bold text-xs uppercase tracking-wide">
                <Percent className="w-4 h-4 text-pink-700" />
                <span>GST Tax Breakdown (18% on Hall Rent)</span>
              </div>
              <div className="grid grid-cols-2 gap-4 text-xs">
                <div className="p-3 bg-white rounded-xl border border-pink-100 flex justify-between">
                  <span className="text-gray-600">SGST (9% on Hall Rent):</span>
                  <span className="font-bold text-pink-950 font-mono">₹{Number(sgst).toLocaleString("en-IN")}</span>
                </div>
                <div className="p-3 bg-white rounded-xl border border-pink-100 flex justify-between">
                  <span className="text-gray-600">CGST (9% on Hall Rent):</span>
                  <span className="font-bold text-pink-950 font-mono">₹{Number(cgst).toLocaleString("en-IN")}</span>
                </div>
              </div>
            </div>
          )}

          {/* Additional Charges */}
          {additionalCharges && additionalCharges.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-xs font-bold uppercase tracking-wider text-pink-950">
                Additional Services & Fees
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                {additionalCharges.map((ac, idx) => (
                  <div key={idx} className="p-3.5 bg-gray-50 rounded-2xl border border-pink-100 flex justify-between items-center">
                    <span className="font-semibold text-gray-800">{ac.name || `Fee #${idx + 1}`}</span>
                    <span className="font-bold text-pink-950 font-mono">₹{Number(ac.amount || 0).toLocaleString("en-IN")}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Grand Totals */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t border-pink-100 text-center">
            <div className="p-5 rounded-2xl bg-pink-100/80 border border-pink-300">
              <span className="text-xs text-pink-950 font-bold block">Total Amount</span>
              <span className="text-2xl font-black text-pink-950 font-mono mt-1 block">
                ₹{Number(total).toLocaleString("en-IN")}
              </span>
            </div>

            <div className="p-5 rounded-2xl bg-emerald-50 border border-emerald-200">
              <span className="text-xs text-emerald-800 font-bold block">Advance Paid</span>
              <span className="text-2xl font-black text-emerald-900 font-mono mt-1 block">
                ₹{Number(advance).toLocaleString("en-IN")}
              </span>
            </div>

            <div className="p-5 rounded-2xl bg-rose-50 border border-rose-200">
              <span className="text-xs text-rose-800 font-bold block">Balance Due</span>
              <span className="text-2xl font-black text-rose-900 font-mono mt-1 block">
                ₹{Number(balance).toLocaleString("en-IN")}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
